<script setup lang="ts">
import SimpleTable from '@/components/tables/SimpleTable.vue';
import { RouteName } from '@/router/models/route-name';
import router from '@/router/router';
import { overlaySpinnerService } from '@/services/app/overlay-spinner-service';
import { supplierInvoiceService } from '@/services/invoices/supplier-invoice-service';
import { models, utils } from 'cesg-shared';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import {
  getSupplierInvoiceColumns,
  sortSupplierInvoices,
  supplierInvoiceAmountKey,
} from './supplier-invoce-table-common';

const { t } = useI18n();
const data = ref<models.SupplierInvoicesResponse[]>([]);

const supplierInvoiceColumns = getSupplierInvoiceColumns(t);
const route = useRoute();

const loadData = async () => {
  overlaySpinnerService.show();
  try {
    data.value = await supplierInvoiceService.getAll(
      utils.type.asType<string>(route.params.id),
      {
        accountNumber: route.query.accountNumber
          ? Number(route.query.accountNumber)
          : undefined,
        ataNumber: route.query.ataNumber
          ? Number(route.query.ataNumber)
          : undefined,
      },
    );
    data.value = sortSupplierInvoices(data.value);
  } finally {
    overlaySpinnerService.hide();
  }
};

watch(
  () => route.path,
  () => {
    loadData();
  },
  { deep: true },
);

const navigateToOverview = async (id: number) => {
  await router.push({
    name: RouteName.InvoiceDetails,
    params: { id: route.params.id, invoiceId: id },
    query: {
      accountNumber: route.query.accountNumber ?? undefined,
      ataNumber: route.query.ataNumber ?? undefined,
    },
  });
};

onMounted(loadData);
</script>
<template>
  <div v-if="route.query.accountNumber || route.query.ataNumber" class="mb-2">
    {{ t('invoices.filter') }}
    <span v-if="route.query.accountNumber">
      {{ t('invoices.filterAccount') }} {{ route.query.accountNumber }}
    </span>
    <span v-if="route.query.ataNumber"
      >, {{ t('invoices.filterATA') }} {{ route.query.ataNumber }}</span
    >
  </div>

  <SimpleTable
    :columns="supplierInvoiceColumns"
    :data="data"
    :sum-column-keys="[supplierInvoiceAmountKey]"
    :enable-search="true"
    :row-clickable="true"
  >
    <template #tbody="{ data }">
      <tr @click="navigateToOverview(row.id)" v-for="row in data" :key="row.id">
        <td
          v-for="(column, index) in supplierInvoiceColumns"
          :key="index"
          :class="[
            column.align ? column.align : '',
            column.class ? column.class(row[column.key]) : '',
          ]"
        >
          <span
            :class="['tag', row[column.key]]"
            v-if="column.key === 'status' && column.format"
          >
            {{ column.format(row[column.key]) }}
          </span>
          <span v-else>
            {{
              column.format ? column.format(row[column.key]) : row[column.key]
            }}
          </span>
        </td>
      </tr>
    </template>
  </SimpleTable>
</template>

<style lang="scss" scoped>
tr {
  background-color: var(--table-light);
}
</style>
